import formatNumber from '@/lib/formatNumber.js';
import { differenceInMilliseconds, isValid } from 'date-fns';
import { parseJsonOrNull } from '@shared/utils/parseJsonOrNull.js';
import { toDisplayJson } from '@shared/utils/toDisplayJson.js';
import { toSafeNumber } from '@shared/utils/toSafeNumber.js';
import { toSafeObjectOrNull } from '@shared/utils/toSafeObjectOrNull.js';

const EMPTY_VALUE = '-';

const SOURCE_LABELS = {
  aws_ecs: 'AWS ECS',
  cloudwatch: 'CloudWatch',
  postgres: 'Postgres',
  tinybird: 'Tinybird',
  webhook: 'Webhook'
};

const HIDDEN_EVENT_LABELS = new Set(['cloudwatch_log']);
const WORKFLOW_RUN_ERROR_PATTERN = /^- Error running workflow - id:/iu;

const padTimePart = (value, size = 2) => String(value).padStart(size, '0');

const formatMemoryMegabytes = (value) => {
  const number = toSafeNumber(value, null);
  if (number === null) {
    return EMPTY_VALUE;
  }

  return `${formatNumber(number)} MB`;
};

const formatRatioPercent = (value) => {
  const number = toSafeNumber(value, null);
  if (number === null) {
    return EMPTY_VALUE;
  }

  return `${Math.round(number * 100)}%`;
};

const formatSeconds = (seconds) => {
  const roundedSeconds = Math.round(seconds * 10) / 10;

  if (Number.isInteger(roundedSeconds)) {
    return String(roundedSeconds);
  }

  return roundedSeconds.toFixed(1);
};

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return isValid(date) ? date : null;
};

const parseJsonMessagePayload = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) {
    return null;
  }

  return parseJsonOrNull(trimmedValue);
};

const parseMemoryUsageDetails = (value) => toSafeObjectOrNull(parseJsonOrNull(value));

const isMemoryUsageRow = (row) => row?.service === 'tinybird.workflow_memory_usage' || row?.event === 'workflow_memory_usage';

export const isCriticalExecutionContextTimelineRow = (row) => {
  if (getExecutionContextTimelineMemoryUsage(row)?.isCritical === true) {
    return true;
  }

  const message = String(row?.message || '').trim();

  return /^FATAL ERROR:/iu.test(message) || WORKFLOW_RUN_ERROR_PATTERN.test(message);
};

const buildMemoryUsageMessage = ({ details, isCritical }) => {
  if (!details) {
    return null;
  }

  return `Heap ${formatRatioPercent(details.heapUsageRatio)}, RSS ${formatRatioPercent(details.rssUsageRatio)}${isCritical ? ' (critical)' : ''}`;
};

export const formatExecutionContextTimestamp = (value) => {
  const date = parseDate(value);
  if (!date) {
    if (!value) {
      return EMPTY_VALUE;
    }

    return String(value);
  }

  return date.toISOString().replace('.000Z', 'Z');
};

export const formatExecutionContextTimelineDate = (value) => {
  const date = parseDate(value);
  if (!date) {
    return EMPTY_VALUE;
  }

  return date.toISOString().slice(0, 10);
};

export const formatExecutionContextTimelineTime = (value) => {
  const date = parseDate(value);
  if (!date) {
    return formatExecutionContextValue(value);
  }

  const milliseconds = date.getUTCMilliseconds();
  const time = [date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()].map((part) => padTimePart(part)).join(':');

  if (!milliseconds) {
    return `${time}Z`;
  }

  return `${time}.${padTimePart(milliseconds, 3)}Z`;
};

export const formatExecutionContextTimelineOffset = (value, baseValue) => {
  const date = parseDate(value);
  const baseDate = parseDate(baseValue);
  if (!date || !baseDate) {
    return null;
  }

  const diffMs = differenceInMilliseconds(date, baseDate);
  if (diffMs === 0) {
    return '+0s';
  }

  const sign = diffMs > 0 ? '+' : '-';
  const absoluteMs = Math.abs(diffMs);
  if (absoluteMs < 1000) {
    return `${sign}${absoluteMs}ms`;
  }

  const totalSeconds = absoluteMs / 1000;
  if (totalSeconds < 60) {
    return `${sign}${formatSeconds(totalSeconds)}s`;
  }

  const totalMinutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  if (totalMinutes < 60) {
    return `${sign}${totalMinutes}m ${formatSeconds(remainingSeconds)}s`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  return `${sign}${totalHours}h ${remainingMinutes}m`;
};

export const formatExecutionContextEventLabel = (event) => {
  if (!event || HIDDEN_EVENT_LABELS.has(event)) {
    return null;
  }

  return String(event).replaceAll('_', ' ');
};

export const formatExecutionContextValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
};

export const getExecutionContextSourceLabel = (source) => SOURCE_LABELS[source] || formatExecutionContextValue(source);

export const getExecutionContextTimelineMessage = (row) => {
  if (isMemoryUsageRow(row)) {
    const details = parseMemoryUsageDetails(row?.data?.details);
    const memoryUsageMessage = buildMemoryUsageMessage({
      details,
      isCritical: Number(row?.data?.isCritical) === 1
    });
    if (memoryUsageMessage) {
      return memoryUsageMessage;
    }
  }

  const parsedMessage = parseJsonMessagePayload(row?.message);
  if (parsedMessage && typeof parsedMessage === 'object' && !Array.isArray(parsedMessage) && parsedMessage.message) {
    return parsedMessage.message;
  }

  return formatExecutionContextValue(row?.message);
};

export const getExecutionContextTimelineRawData = (row) => {
  const parsedMessage = parseJsonMessagePayload(row?.message);
  if (!parsedMessage) {
    return row?.data;
  }

  if (!row?.data) {
    return parsedMessage;
  }

  return {
    ...row.data,
    parsedMessage
  };
};

export const getExecutionContextTimelineMemoryUsage = (row) => {
  if (!isMemoryUsageRow(row)) {
    return null;
  }

  const details = parseMemoryUsageDetails(row?.data?.details);
  if (!details) {
    return null;
  }

  const isCritical = Number(row?.data?.isCritical) === 1;

  return {
    isCritical,
    items: [
      {
        label: 'Heap',
        value: `${formatMemoryMegabytes(details.heapUsed)} / ${formatMemoryMegabytes(details.maxOldSpaceSize)}`,
        detail: formatRatioPercent(details.heapUsageRatio)
      },
      {
        label: 'RSS',
        value: `${formatMemoryMegabytes(details.rss)} / ${formatMemoryMegabytes(details.totalMem)}`,
        detail: formatRatioPercent(details.rssUsageRatio)
      },
      {
        label: 'Critical',
        value: isCritical ? 'Yes' : 'No',
        detail: ''
      }
    ]
  };
};

export const buildExecutionContextSummaryItems = (context) => {
  const workflowLog = context?.meta?.workflowLog || {};
  const webhook = context?.meta?.webhook || {};
  const timeWindow = context?.meta?.timeWindow || {};

  const items = [
    { label: 'Workflow log', value: workflowLog.id },
    { label: 'Workflow', value: workflowLog.workflowName || workflowLog.workflowId },
    { label: 'Status', value: workflowLog.statusLabel },
    { label: 'Environment', value: workflowLog.environmentId },
    { label: 'Installation', value: workflowLog.installationId },
    { label: 'End user', value: workflowLog.endUserId },
    { label: 'Invoked by', value: workflowLog.invokedBy },
    { label: 'Runner', value: [workflowLog.runnerSize, workflowLog.runnerNetwork].filter(Boolean).join(' / ') },
    { label: 'Started', value: formatExecutionContextTimestamp(workflowLog.startedOn) },
    { label: 'Updated', value: formatExecutionContextTimestamp(workflowLog.updatedOn) },
    { label: 'Finished', value: formatExecutionContextTimestamp(workflowLog.finishedOn) },
    { label: 'Window', value: `${formatExecutionContextTimestamp(timeWindow.start)} - ${formatExecutionContextTimestamp(timeWindow.end)}` }
  ];

  if (webhook.receivedOn) {
    items.splice(7, 0, { label: 'Webhook received', value: formatExecutionContextTimestamp(webhook.receivedOn) });
  }

  return items.map((item) => ({
    ...item,
    value: formatExecutionContextValue(item.value)
  }));
};

export const buildExecutionContextSourceItems = (context) => {
  const sources = context?.meta?.sources || {};
  const tinybirdSources = Object.entries(sources.tinybird || {}).map(([sourceName, result]) => ({
    label: `Tinybird ${sourceName}`,
    value: `${result?.rowCount || 0} rows${result?.success === false ? ' / failed' : ''}`
  }));

  return [{ label: 'Lambda logs', value: sources.cloudwatch?.lambda?.eventCount }, { label: 'ECS logs', value: sources.cloudwatch?.ecs?.eventCount }, { label: 'ECS task describes', value: `${sources.ecsTasks?.foundCount || 0}/${sources.ecsTasks?.requestedCount || 0}` }, ...tinybirdSources].map((item) => ({
    ...item,
    value: formatExecutionContextValue(item.value)
  }));
};

export const stringifyExecutionContextData = (value) => {
  if (!value) {
    return null;
  }

  return toDisplayJson(value);
};
